import { chargeFieldLabel, formatMoney, getCurrencySymbol } from '../utils/currency';
/** Delivery hub master rules (slots, packaging, partner pricing). */
import type { MasterFieldDef } from './masters/types';
import { resolveRecordSortOrder } from './foodMasterRules';
import { resolveAssetUrl } from '../utils/assetUrl';
import { FOOD_ADDON_PLACEHOLDER_DATA_URI } from '../utils/foodMasterImages';
import { validateOptionalDescription } from '../utils/descriptionValidation';
import {
  ZONE_DISTANCE_DECIMALS,
  ZONE_DISTANCE_MAX,
  ZONE_DISTANCE_MIN,
  ZONE_PRICE_DECIMALS,
  ZONE_PRICE_MAX,
  ZONE_PRICE_MIN,
} from './transportPartnerFlow';

export const PARTNER_PRICING_AMOUNT_MIN = ZONE_PRICE_MIN;
export const PARTNER_PRICING_AMOUNT_MAX = ZONE_PRICE_MAX;
export const PARTNER_PRICING_AMOUNT_DECIMALS = ZONE_PRICE_DECIMALS;
export const PARTNER_PRICING_KM_MIN = ZONE_DISTANCE_MIN;
export const PARTNER_PRICING_KM_MAX = ZONE_DISTANCE_MAX;
export const PARTNER_PRICING_KM_DECIMALS = ZONE_DISTANCE_DECIMALS;
/** Base KM must be greater than zero — use a small positive floor (matches schema min). */
export const PARTNER_PRICING_BASE_KM_MIN = 0.01;
export const PARTNER_PRICING_EXAMPLE_DISTANCE_KM = 8;
const PACKAGING_MASTER_TYPES = [
  'packaging_types',
] as const;

const GROCERY_STORE_TYPE_MASTER_TYPES = [
  'grocery_store_types',
] as const;

const GROCERY_CATEGORY_MASTER_TYPES = [
  'grocery_categories',
] as const;

const GROCERY_SUB_CATEGORY_MASTER_TYPES = [
  'grocery_sub_categories',
] as const;

const GROCERY_BRAND_MASTER_TYPES = [
  'grocery_brands',
] as const;

const WAREHOUSE_MASTER_TYPES = [
  'warehouses',
] as const;

const PRODUCT_VARIANT_MASTER_TYPES = [
  'product_variants',
] as const;

const WEIGHT_TYPE_MASTER_TYPES = [
  'weight_types',
] as const;

const SUPPLIER_MASTER_TYPES = [
  'suppliers',
] as const;

const STOCK_ALERT_MASTER_TYPES = [
  'stock_alerts',
] as const;

const GROCERY_TAX_GROUP_MASTER_TYPES = [
  'grocery_tax_groups',
] as const;

const GROCERY_PRODUCT_IMAGE_MASTER_TYPES = [
  'grocery_product_images',
] as const;

const GROCERY_AVAILABILITY_SCHEDULE_MASTER_TYPES = [
  'grocery_availability_schedules',
] as const;

const PARTNER_PRICING_MASTER_TYPES = [
  'delivery_partner_pricing',
] as const;

const INCENTIVE_MASTER_TYPES = [
  'delivery_incentives',
] as const;

const PARTNER_TIER_MASTER_TYPES = [
  'delivery_partners_tiers',
] as const;

const MERCHANT_DOCUMENT_MASTER_TYPES = [
  'merchant_document_types',
] as const;

const CANCELLATION_REASON_MASTER_TYPES = [
  'cancellation_reasons',
] as const;

const SUBSTITUTION_RULE_MASTER_TYPES = [
  'substitution_rules',
] as const;

const PORTER_MASTER_TYPES = [
  'porter_special_handling',
  'porter_cancellation_reasons',
  'porter_document_types',
] as const;

/** Delivery master types that auto-generate code from display name (create flow). */
export const DELIVERY_AUTO_CODE_TYPES = new Set<string>([
  'delivery_slots',
  ...PACKAGING_MASTER_TYPES,
  ...GROCERY_STORE_TYPE_MASTER_TYPES,
  ...GROCERY_CATEGORY_MASTER_TYPES,
  ...GROCERY_SUB_CATEGORY_MASTER_TYPES,
  ...GROCERY_BRAND_MASTER_TYPES,
  ...WAREHOUSE_MASTER_TYPES,
  ...PRODUCT_VARIANT_MASTER_TYPES,
  ...WEIGHT_TYPE_MASTER_TYPES,
  ...SUPPLIER_MASTER_TYPES,
  ...STOCK_ALERT_MASTER_TYPES,
  ...GROCERY_TAX_GROUP_MASTER_TYPES,
  ...GROCERY_PRODUCT_IMAGE_MASTER_TYPES,
  ...GROCERY_AVAILABILITY_SCHEDULE_MASTER_TYPES,
  ...PARTNER_PRICING_MASTER_TYPES,
  ...INCENTIVE_MASTER_TYPES,
  ...PARTNER_TIER_MASTER_TYPES,
  ...MERCHANT_DOCUMENT_MASTER_TYPES,
  ...CANCELLATION_REASON_MASTER_TYPES,
  ...SUBSTITUTION_RULE_MASTER_TYPES,
  ...PORTER_MASTER_TYPES,
]);

/** Delivery master types that hide the Active toggle on the add/edit form. */
export const DELIVERY_HIDE_STATUS_TOGGLE_TYPES = new Set<string>([
  'delivery_slots',
  ...PACKAGING_MASTER_TYPES,
  ...GROCERY_STORE_TYPE_MASTER_TYPES,
  ...GROCERY_CATEGORY_MASTER_TYPES,
  ...GROCERY_SUB_CATEGORY_MASTER_TYPES,
  ...GROCERY_BRAND_MASTER_TYPES,
  ...WAREHOUSE_MASTER_TYPES,
  ...PRODUCT_VARIANT_MASTER_TYPES,
  ...WEIGHT_TYPE_MASTER_TYPES,
  ...SUPPLIER_MASTER_TYPES,
  ...STOCK_ALERT_MASTER_TYPES,
  ...GROCERY_TAX_GROUP_MASTER_TYPES,
  ...GROCERY_PRODUCT_IMAGE_MASTER_TYPES,
  ...GROCERY_AVAILABILITY_SCHEDULE_MASTER_TYPES,
  ...PARTNER_PRICING_MASTER_TYPES,
  ...INCENTIVE_MASTER_TYPES,
  ...PARTNER_TIER_MASTER_TYPES,
  ...MERCHANT_DOCUMENT_MASTER_TYPES,
  ...CANCELLATION_REASON_MASTER_TYPES,
  ...SUBSTITUTION_RULE_MASTER_TYPES,
  ...PORTER_MASTER_TYPES,
]);

/** Delivery master types with description spam validation (repeated chars/words). */
export const DELIVERY_DESCRIPTION_VALIDATED_TYPES = new Set<string>([
  'delivery_slots',
  ...PACKAGING_MASTER_TYPES,
  ...GROCERY_STORE_TYPE_MASTER_TYPES,
  ...GROCERY_CATEGORY_MASTER_TYPES,
  ...GROCERY_SUB_CATEGORY_MASTER_TYPES,
  ...GROCERY_BRAND_MASTER_TYPES,
  ...WAREHOUSE_MASTER_TYPES,
  ...PRODUCT_VARIANT_MASTER_TYPES,
  ...WEIGHT_TYPE_MASTER_TYPES,
  ...SUPPLIER_MASTER_TYPES,
  ...STOCK_ALERT_MASTER_TYPES,
  ...GROCERY_TAX_GROUP_MASTER_TYPES,
  ...GROCERY_PRODUCT_IMAGE_MASTER_TYPES,
  ...GROCERY_AVAILABILITY_SCHEDULE_MASTER_TYPES,
  ...PARTNER_PRICING_MASTER_TYPES,
  ...INCENTIVE_MASTER_TYPES,
  ...PARTNER_TIER_MASTER_TYPES,
  ...MERCHANT_DOCUMENT_MASTER_TYPES,
  ...CANCELLATION_REASON_MASTER_TYPES,
  ...SUBSTITUTION_RULE_MASTER_TYPES,
  ...PORTER_MASTER_TYPES,
]);

/** Delivery master types whose list table shows active/inactive as a switch. */
export const DELIVERY_STATUS_TOGGLE_LIST_TYPES = new Set<string>([
  'delivery_slots',
  ...PACKAGING_MASTER_TYPES,
  ...GROCERY_STORE_TYPE_MASTER_TYPES,
  ...GROCERY_CATEGORY_MASTER_TYPES,
  ...GROCERY_SUB_CATEGORY_MASTER_TYPES,
  ...GROCERY_BRAND_MASTER_TYPES,
  ...WAREHOUSE_MASTER_TYPES,
  ...PRODUCT_VARIANT_MASTER_TYPES,
  ...WEIGHT_TYPE_MASTER_TYPES,
  ...SUPPLIER_MASTER_TYPES,
  ...STOCK_ALERT_MASTER_TYPES,
  ...GROCERY_TAX_GROUP_MASTER_TYPES,
  ...GROCERY_PRODUCT_IMAGE_MASTER_TYPES,
  ...GROCERY_AVAILABILITY_SCHEDULE_MASTER_TYPES,
  ...PARTNER_PRICING_MASTER_TYPES,
  ...INCENTIVE_MASTER_TYPES,
  ...PARTNER_TIER_MASTER_TYPES,
  ...MERCHANT_DOCUMENT_MASTER_TYPES,
  ...CANCELLATION_REASON_MASTER_TYPES,
  ...SUBSTITUTION_RULE_MASTER_TYPES,
  ...PORTER_MASTER_TYPES,
]);

/** Delivery master types whose sort order must be a whole number ≥ 1. */
export const DELIVERY_INTEGER_SORT_ORDER_TYPES = new Set<string>([
  'delivery_slots',
  ...PACKAGING_MASTER_TYPES,
  ...GROCERY_STORE_TYPE_MASTER_TYPES,
  ...GROCERY_CATEGORY_MASTER_TYPES,
  ...GROCERY_SUB_CATEGORY_MASTER_TYPES,
  ...GROCERY_BRAND_MASTER_TYPES,
  ...WAREHOUSE_MASTER_TYPES,
  ...PRODUCT_VARIANT_MASTER_TYPES,
  ...WEIGHT_TYPE_MASTER_TYPES,
  ...SUPPLIER_MASTER_TYPES,
  ...STOCK_ALERT_MASTER_TYPES,
  ...GROCERY_TAX_GROUP_MASTER_TYPES,
  ...GROCERY_PRODUCT_IMAGE_MASTER_TYPES,
  ...GROCERY_AVAILABILITY_SCHEDULE_MASTER_TYPES,
  ...PARTNER_PRICING_MASTER_TYPES,
  ...INCENTIVE_MASTER_TYPES,
  ...PARTNER_TIER_MASTER_TYPES,
  ...MERCHANT_DOCUMENT_MASTER_TYPES,
  ...CANCELLATION_REASON_MASTER_TYPES,
  ...SUBSTITUTION_RULE_MASTER_TYPES,
  ...PORTER_MASTER_TYPES,
]);

/** Delivery master types whose sort order is sequential on create. */
export const DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES = new Set<string>([
  'delivery_slots',
  ...PACKAGING_MASTER_TYPES,
  ...GROCERY_STORE_TYPE_MASTER_TYPES,
  ...GROCERY_CATEGORY_MASTER_TYPES,
  ...GROCERY_BRAND_MASTER_TYPES,
  ...WAREHOUSE_MASTER_TYPES,
  ...PRODUCT_VARIANT_MASTER_TYPES,
  ...WEIGHT_TYPE_MASTER_TYPES,
  ...SUPPLIER_MASTER_TYPES,
  ...STOCK_ALERT_MASTER_TYPES,
  ...GROCERY_TAX_GROUP_MASTER_TYPES,
  ...GROCERY_PRODUCT_IMAGE_MASTER_TYPES,
  ...GROCERY_AVAILABILITY_SCHEDULE_MASTER_TYPES,
  // Sub-categories: next order is parent-scoped (handled in the form), not global.
  ...PARTNER_PRICING_MASTER_TYPES,
  ...INCENTIVE_MASTER_TYPES,
  ...PARTNER_TIER_MASTER_TYPES,
  ...MERCHANT_DOCUMENT_MASTER_TYPES,
  ...CANCELLATION_REASON_MASTER_TYPES,
  ...SUBSTITUTION_RULE_MASTER_TYPES,
  ...PORTER_MASTER_TYPES,
]);

/** Delivery master types whose sort order must be unique across records. */
export const DELIVERY_UNIQUE_SORT_ORDER_TYPES = new Set<string>([
  'delivery_slots',
  ...PACKAGING_MASTER_TYPES,
  ...GROCERY_STORE_TYPE_MASTER_TYPES,
  ...GROCERY_CATEGORY_MASTER_TYPES,
  ...GROCERY_BRAND_MASTER_TYPES,
  ...WAREHOUSE_MASTER_TYPES,
  ...PRODUCT_VARIANT_MASTER_TYPES,
  ...WEIGHT_TYPE_MASTER_TYPES,
  ...SUPPLIER_MASTER_TYPES,
  ...STOCK_ALERT_MASTER_TYPES,
  ...GROCERY_TAX_GROUP_MASTER_TYPES,
  ...GROCERY_PRODUCT_IMAGE_MASTER_TYPES,
  ...GROCERY_AVAILABILITY_SCHEDULE_MASTER_TYPES,
  ...PARTNER_PRICING_MASTER_TYPES,
  ...INCENTIVE_MASTER_TYPES,
  ...PARTNER_TIER_MASTER_TYPES,
  ...MERCHANT_DOCUMENT_MASTER_TYPES,
  ...CANCELLATION_REASON_MASTER_TYPES,
  ...SUBSTITUTION_RULE_MASTER_TYPES,
  ...PORTER_MASTER_TYPES,
]);

/** Delivery master types whose sort order must be unique within the same parent. */
export const DELIVERY_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES = new Set<string>([
  ...GROCERY_SUB_CATEGORY_MASTER_TYPES,
]);

const PLACEHOLDER_PRICING_NAME_RE = /^(test|default|sample|demo|asdf|xxx|temp)(\s|$)/i;
const PLACEHOLDER_PRICING_DESC_RE = /^(test|default|sample|demo|asdf|xxx|temp|n\/?a|-)?\.?$/i;

const SERVICE_SLUG_LABELS: Record<string, string> = {
  'food-delivery': 'Food Delivery',
  'grocery-delivery': 'Grocery Delivery',
};
/** Half-hour delivery window options for Start/End time dropdowns. */
export const DELIVERY_TIME_OPTIONS: Array<{ value: string; label: string }> = Array.from(
  { length: 48 },
  (_, index) => {
    const hours = Math.floor(index / 2);
    const minutes = index % 2 === 0 ? '00' : '30';
    const value = `${String(hours).padStart(2, '0')}:${minutes}`;
    return { value, label: value };
  },
);

/** Slot fee bounds (₹) — free slots use 0; paid express-style fees stay within this cap. */
export const DELIVERY_SLOT_FEE_MIN = 0;
export const DELIVERY_SLOT_FEE_MAX = 9999;

/** Packaging fee bounds (₹) — free packaging uses 0; paid types stay within this cap. */
export const PACKAGING_FEE_MIN = 0;
export const PACKAGING_FEE_MAX = 9999;

function squareImage(photoId: string): string {
  return `https://images.unsplash.com/${photoId}?w=400&h=400&fit=crop&auto=format`;
}

/** Default packaging images by code — used when stored URLs are missing or broken. */
export const PACKAGING_DEFAULT_IMAGES: Record<string, string> = {
  standard: squareImage('photo-1604719312566-8912e9227c6a'),
  eco: squareImage('photo-1578662996442-48f60103fc96'),
  premium_insulated: squareImage('photo-1553062407-98eeb64c6a62'),
};

export const GENERIC_PACKAGING_IMAGE = squareImage('photo-1602143407151-7111542de6e8');

const BROKEN_PACKAGING_IMAGE_IDS = [
  'photo-1618477388954-7852f7259472',
  'photo-1584464491033-06628f3a6fb7',
];

export function isBrokenPackagingImageUrl(url: string): boolean {
  const value = url.trim();
  if (!value) return false;
  return BROKEN_PACKAGING_IMAGE_IDS.some((id) => value.includes(id));
}

export function resolvePackagingSampleImageUrl(code: string): string {
  const key = code.trim().toLowerCase();
  return PACKAGING_DEFAULT_IMAGES[key] || GENERIC_PACKAGING_IMAGE;
}

export function resolvePackagingListImageUrl(
  code: string,
  metadata?: Record<string, unknown>,
): { primary: string; fallback: string } {
  const raw = String(metadata?.imageUrl ?? metadata?.icon ?? metadata?.image ?? '').trim();
  const defaultRaw = resolvePackagingSampleImageUrl(code);
  const defaultResolved = resolveAssetUrl(defaultRaw);
  const genericResolved = resolveAssetUrl(GENERIC_PACKAGING_IMAGE);

  if (raw && !isBrokenPackagingImageUrl(raw)) {
    return {
      primary: resolveAssetUrl(raw),
      fallback: defaultResolved !== genericResolved ? defaultResolved : genericResolved,
    };
  }
  return {
    primary: defaultResolved,
    fallback: genericResolved !== defaultResolved ? genericResolved : '',
  };
}

export function resolvePackagingImageSources(
  code: string,
  metadata?: Record<string, unknown>,
): string[] {
  const { primary, fallback } = resolvePackagingListImageUrl(code, metadata);
  const generic = resolveAssetUrl(GENERIC_PACKAGING_IMAGE);
  const chain = [primary, fallback, generic, FOOD_ADDON_PLACEHOLDER_DATA_URI];
  return chain.filter((src, index) => src && chain.indexOf(src) === index);
}

export function formatPackagingFeeDisplay(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  if (num === 0) return `Free (${formatMoney(0)})`;
  return formatMoney(num);
}

export function resolveDeliverySlotStart(row: {
  metadata?: Record<string, unknown>;
}): string {
  return String(row.metadata?.start ?? row.metadata?.startTime ?? '').trim();
}

export function resolveDeliverySlotEnd(row: {
  metadata?: Record<string, unknown>;
}): string {
  return String(row.metadata?.end ?? row.metadata?.endTime ?? '').trim();
}

/** Sort delivery slots by display order, then chronologically by start time. */
export function sortDeliverySlotRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const orderA = resolveRecordSortOrder(a);
    const orderB = resolveRecordSortOrder(b);
    if (orderA !== orderB) {
      if (orderA === 0) return 1;
      if (orderB === 0) return -1;
      return orderA - orderB;
    }
    const startA = resolveDeliverySlotStart(a);
    const startB = resolveDeliverySlotStart(b);
    if (startA !== startB) return startA.localeCompare(startB);
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });
}

export function sortPackagingTypeRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const orderA = resolveRecordSortOrder(a);
    const orderB = resolveRecordSortOrder(b);
    if (orderA !== orderB) {
      if (orderA === 0) return 1;
      if (orderB === 0) return -1;
      return orderA - orderB;
    }
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });
}

/** Grocery Store Types list — Display Order ascending, then name. */
export function sortGroceryStoreTypeRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Grocery Categories list — Display Order ascending, then name. */
export function sortGroceryCategoryRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Default grocery category images by code — replaces missing/404 seed Unsplash URLs. */
export const GROCERY_CATEGORY_DEFAULT_IMAGES: Record<string, string> = {
  // Prefer IDs already used successfully in seed / food masters.
  fruits: squareImage('photo-1563636619-e9143da7973b'),
  dairy: squareImage('photo-1563636619-e9143da7973b'),
  snacks: squareImage('photo-1544145945-f90425340c7e'),
  household: squareImage('photo-1563453392212-326f5e854473'),
};

export const GENERIC_GROCERY_CATEGORY_IMAGE = squareImage('photo-1563453392212-326f5e854473');

/** Legacy Unsplash IDs that 404 for seeded grocery category rows. */
const BROKEN_GROCERY_CATEGORY_IMAGE_IDS = [
  'photo-1488459716781-31f6589a3cd6',
  'photo-1566478989037-101a29648484',
];

export function isBrokenGroceryCategoryImageUrl(url: string): boolean {
  const value = url.trim();
  if (!value) return false;
  return BROKEN_GROCERY_CATEGORY_IMAGE_IDS.some((id) => value.includes(id));
}

export function resolveGroceryCategorySampleImageUrl(code: string): string {
  const key = code.trim().toLowerCase();
  return GROCERY_CATEGORY_DEFAULT_IMAGES[key] || GENERIC_GROCERY_CATEGORY_IMAGE;
}

export function resolveGroceryCategoryListImageUrl(
  code: string,
  metadata?: Record<string, unknown>,
): { primary: string; fallback: string } {
  const raw = String(metadata?.imageUrl ?? metadata?.icon ?? metadata?.image ?? '').trim();
  const defaultRaw = resolveGroceryCategorySampleImageUrl(code);
  const defaultResolved = resolveAssetUrl(defaultRaw);
  const genericResolved = resolveAssetUrl(GENERIC_GROCERY_CATEGORY_IMAGE);

  if (raw && !isBrokenGroceryCategoryImageUrl(raw)) {
    return {
      primary: resolveAssetUrl(raw),
      fallback: defaultResolved !== genericResolved ? defaultResolved : genericResolved,
    };
  }
  return {
    primary: defaultResolved,
    fallback: genericResolved !== defaultResolved ? genericResolved : '',
  };
}

export function resolveGroceryCategoryImageSources(
  code: string,
  metadata?: Record<string, unknown>,
): string[] {
  const { primary, fallback } = resolveGroceryCategoryListImageUrl(code, metadata);
  const generic = resolveAssetUrl(GENERIC_GROCERY_CATEGORY_IMAGE);
  const chain = [primary, fallback, generic, FOOD_ADDON_PLACEHOLDER_DATA_URI];
  return chain.filter((src, index) => src && chain.indexOf(src) === index);
}

/** Grocery Sub Categories list — parent category, then Display Order, then name. */
export function resolveGrocerySubCategoryParentCode(row: {
  parentCategoryCode?: string;
  metadata?: Record<string, unknown>;
}): string {
  return String(row.metadata?.parentCategoryCode ?? row.parentCategoryCode ?? '').trim().toLowerCase();
}

export function sortGrocerySubCategoryRows<T extends {
  name?: string;
  sortOrder?: number;
  parentCategoryCode?: string;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const parentCmp = resolveGrocerySubCategoryParentCode(a).localeCompare(resolveGrocerySubCategoryParentCode(b));
    if (parentCmp !== 0) return parentCmp;
    const orderA = resolveRecordSortOrder(a);
    const orderB = resolveRecordSortOrder(b);
    if (orderA !== orderB) {
      if (orderA === 0) return 1;
      if (orderB === 0) return -1;
      return orderA - orderB;
    }
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });
}

/** Default grocery sub-category images by code — replaces missing/404 seed Unsplash URLs. */
export const GROCERY_SUB_CATEGORY_DEFAULT_IMAGES: Record<string, string> = {
  apple: squareImage('photo-1560806887-1e4cd0b6cbd6'),
  banana: squareImage('photo-1563636619-e9143da7973b'),
  leafy: squareImage('photo-1563453392212-326f5e854473'),
  milk: squareImage('photo-1563636619-e9143da7973b'),
  bread_eggs: squareImage('photo-1509440159596-0249088772ff'),
  chips: squareImage('photo-1544145945-f90425340c7e'),
  beverages: squareImage('photo-1544145945-f90425340c7e'),
  cleaners: squareImage('photo-1563453392212-326f5e854473'),
  detergents: squareImage('photo-1563453392212-326f5e854473'),
};

export const GENERIC_GROCERY_SUB_CATEGORY_IMAGE = GENERIC_GROCERY_CATEGORY_IMAGE;

/** Extra broken Unsplash IDs seen on grocery sub-category seeds. */
const BROKEN_GROCERY_SUB_CATEGORY_IMAGE_IDS = [
  ...BROKEN_GROCERY_CATEGORY_IMAGE_IDS,
  'photo-1546094096-0df4bcaaa337',
  'photo-1629203851122-3726ecdf080e',
];

export function isBrokenGrocerySubCategoryImageUrl(url: string): boolean {
  const value = url.trim();
  if (!value) return false;
  return BROKEN_GROCERY_SUB_CATEGORY_IMAGE_IDS.some((id) => value.includes(id))
    || isBrokenGroceryCategoryImageUrl(value);
}

export function resolveGrocerySubCategorySampleImageUrl(code: string): string {
  const key = code.trim().toLowerCase();
  return GROCERY_SUB_CATEGORY_DEFAULT_IMAGES[key] || GENERIC_GROCERY_SUB_CATEGORY_IMAGE;
}

export function resolveGrocerySubCategoryListImageUrl(
  code: string,
  metadata?: Record<string, unknown>,
): { primary: string; fallback: string } {
  const raw = String(metadata?.imageUrl ?? metadata?.icon ?? metadata?.image ?? '').trim();
  const defaultRaw = resolveGrocerySubCategorySampleImageUrl(code);
  const defaultResolved = resolveAssetUrl(defaultRaw);
  const genericResolved = resolveAssetUrl(GENERIC_GROCERY_SUB_CATEGORY_IMAGE);

  if (raw && !isBrokenGrocerySubCategoryImageUrl(raw)) {
    return {
      primary: resolveAssetUrl(raw),
      fallback: defaultResolved !== genericResolved ? defaultResolved : genericResolved,
    };
  }
  return {
    primary: defaultResolved,
    fallback: genericResolved !== defaultResolved ? genericResolved : '',
  };
}

export function resolveGrocerySubCategoryImageSources(
  code: string,
  metadata?: Record<string, unknown>,
): string[] {
  const { primary, fallback } = resolveGrocerySubCategoryListImageUrl(code, metadata);
  const generic = resolveAssetUrl(GENERIC_GROCERY_SUB_CATEGORY_IMAGE);
  const chain = [primary, fallback, generic, FOOD_ADDON_PLACEHOLDER_DATA_URI];
  return chain.filter((src, index) => src && chain.indexOf(src) === index);
}

/** Next display order among grocery sub-categories under the same parent. */
export function getNextGrocerySubCategorySortOrder(
  records: Array<{ sortOrder?: number; parentCategoryCode?: string; metadata?: Record<string, unknown> }>,
  parentCategoryCode: string,
): number {
  const parent = parentCategoryCode.trim().toLowerCase();
  if (!parent) return 1;
  const siblings = records.filter((row) => resolveGrocerySubCategoryParentCode(row) === parent);
  const orders = siblings.map((row) => resolveRecordSortOrder(row)).filter((n) => n >= 1);
  if (!orders.length) return 1;
  return Math.max(...orders) + 1;
}

/** Grocery Sub Categories form — image + parent; status/sort live on list toggle / basic info. */
export function enrichGrocerySubCategoryMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  const hasParent = fields.some((field) => field.key === 'parentCategoryCode');
  const base = hasParent
    ? fields
    : [
      {
        key: 'parentCategoryCode',
        label: 'Parent Category',
        type: 'master' as const,
        masterType: 'grocery_categories',
        required: true,
        helperText: 'Select parent grocery category',
      },
      ...fields,
    ];
  return base
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'parentCategoryCode') {
        return {
          ...field,
          type: 'master' as const,
          masterType: 'grocery_categories',
          required: true,
          label: field.label || 'Parent Category',
          helperText: field.helperText || 'Select parent grocery category',
        };
      }
      if (field.key === 'imageUrl' || field.type === 'image') {
        return {
          ...field,
          key: 'imageUrl',
          label: 'Sub Category Image',
          type: 'image' as const,
          required: true,
          helperText: 'PNG, JPEG, or WebP — shown under the parent grocery category',
        };
      }
      return field;
    });
}

/** Store Types form — image stays in metadata; status/sort live on basic info / list toggle. */
export function enrichGroceryStoreTypeMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'imageUrl' || field.type === 'image') {
        return {
          ...field,
          key: 'imageUrl',
          label: 'Store Type Image',
          type: 'image' as const,
          required: true,
          helperText: 'PNG, JPEG, or WebP — logo shown when creating grocery stores',
        };
      }
      return field;
    });
}

/** Grocery Categories form — image only in metadata; status/sort/description live on basic info / list toggle. */
export function enrichGroceryCategoryMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder' && field.key !== 'description')
    .map((field) => {
      if (field.key === 'imageUrl' || field.type === 'image') {
        return {
          ...field,
          key: 'imageUrl',
          label: 'Category Image',
          type: 'image' as const,
          required: true,
          helperText: 'PNG, JPEG, or WebP — shown in grocery catalog and store menus',
        };
      }
      return field;
    });
}

/** Grocery Brands list — Display Order ascending, then name. */
export function sortGroceryBrandRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Brand Master form — image stays in metadata; status/sort live on list toggle / basic info. */
export function enrichGroceryBrandMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'imageUrl' || field.type === 'image') {
        return {
          ...field,
          key: 'imageUrl',
          label: 'Brand Logo',
          type: 'image' as const,
          required: true,
          helperText: 'PNG, JPEG, or WebP — brand logo shown in grocery browse',
        };
      }
      return field;
    });
}

/** Warehouses list — Display Order ascending, then name. */
export function sortWarehouseRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Warehouse form — image + required address; status/sort live on list toggle / basic info. */
export function enrichWarehouseMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'imageUrl' || field.type === 'image') {
        return {
          ...field,
          key: 'imageUrl',
          label: 'Warehouse Image',
          type: 'image' as const,
          required: false,
          helperText: 'Optional PNG, JPEG, or WebP of the fulfillment center',
        };
      }
      if (field.key === 'address') {
        return {
          ...field,
          required: true,
          label: field.label || 'Address',
          helperText: field.helperText || 'Street / area address for this warehouse — required',
        };
      }
      return field;
    });
}

/** Product variants list — Display Order ascending, then name. */
export function sortProductVariantRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Product Variant form — status/sort live on list toggle / basic info. */
export function enrichProductVariantMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields.filter((field) => field.key !== 'status' && field.key !== 'sortOrder');
}

/** Weight types list — Display Order ascending, then name. */
export function sortWeightTypeRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Weight Type form — status/sort live on list toggle / basic info. */
export function enrichWeightTypeMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields.filter((field) => field.key !== 'status' && field.key !== 'sortOrder');
}

/** Suppliers list — Display Order ascending, then name. */
export function sortSupplierRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Supplier form — phone/email stay; status/sort live on list toggle / basic info. */
export function enrichSupplierMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder' && field.key !== 'phoneCountryCode')
    .map((field) => {
      if (field.key === 'phone') {
        return {
          ...field,
          label: field.label || 'Phone',
          helperText: field.helperText || 'Select country code, then enter the local phone number',
        };
      }
      if (field.key === 'email') {
        return {
          ...field,
          label: field.label || 'Email',
          helperText: field.helperText || 'Optional supplier contact email',
        };
      }
      return field;
    });
}

export const STOCK_ALERT_THRESHOLD_MIN = 1;
export const STOCK_ALERT_THRESHOLD_MAX = 999_999;

/** Stock alerts list — Display Order ascending, then name. */
export function sortStockAlertRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Stock Alert form — required threshold with limits; status/sort live on list toggle / basic info. */
export function enrichStockAlertMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'threshold') {
        return {
          ...field,
          required: true,
          type: 'number' as const,
          min: STOCK_ALERT_THRESHOLD_MIN,
          max: STOCK_ALERT_THRESHOLD_MAX,
          label: field.label || 'Threshold',
          helperText: field.helperText
            || `Whole number from ${STOCK_ALERT_THRESHOLD_MIN} to ${STOCK_ALERT_THRESHOLD_MAX.toLocaleString('en-IN')} — alert when stock falls to this level`,
        };
      }
      return field;
    });
}

export function validateStockAlertThreshold(value: unknown): string | undefined {
  if (value === undefined || value === null || String(value).trim() === '') {
    return 'Threshold is required';
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Threshold must be a number';
  if (!Number.isInteger(num)) return 'Threshold must be a whole number (no decimals)';
  if (num < STOCK_ALERT_THRESHOLD_MIN) return `Threshold must be at least ${STOCK_ALERT_THRESHOLD_MIN}`;
  if (num > STOCK_ALERT_THRESHOLD_MAX) {
    return `Threshold must be at most ${STOCK_ALERT_THRESHOLD_MAX.toLocaleString('en-IN')}`;
  }
  return undefined;
}

/** Grocery tax groups list — Display Order ascending, then name. */
export function sortGroceryTaxGroupRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Grocery Tax Group form — tax % 0–100; status/sort live on list toggle / basic info. */
export function enrichGroceryTaxGroupMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'taxPercent') {
        return {
          ...field,
          required: true,
          type: 'percent' as const,
          min: 0,
          max: 100,
          label: field.label || 'Tax %',
          helperText: field.helperText || 'Enter a value from 0 to 100 — display name becomes e.g. GST 5%',
        };
      }
      if (field.key === 'taxIncluded') {
        return {
          ...field,
          helperText: field.helperText || 'Yes if tax is already included in the product price',
        };
      }
      return field;
    });
}

/** Thumbnail vs gallery image size guidance for Product Image Master. */
export const GROCERY_PRODUCT_IMAGE_THUMBNAIL_MAX = { width: 800, height: 800 } as const;
export const GROCERY_PRODUCT_IMAGE_GALLERY_MAX = { width: 1200, height: 800 } as const;

export function groceryProductImageConstraintsForType(imageType: unknown): {
  imageMaxWidth: number;
  imageMaxHeight: number;
  imageAspectRatio?: '1:1';
  helperText: string;
} {
  const type = String(imageType ?? '').trim().toLowerCase();
  if (type === 'gallery') {
    return {
      imageMaxWidth: GROCERY_PRODUCT_IMAGE_GALLERY_MAX.width,
      imageMaxHeight: GROCERY_PRODUCT_IMAGE_GALLERY_MAX.height,
      helperText: `Gallery · PNG, JPEG, or WebP · max ${GROCERY_PRODUCT_IMAGE_GALLERY_MAX.width}×${GROCERY_PRODUCT_IMAGE_GALLERY_MAX.height}px`,
    };
  }
  return {
    imageMaxWidth: GROCERY_PRODUCT_IMAGE_THUMBNAIL_MAX.width,
    imageMaxHeight: GROCERY_PRODUCT_IMAGE_THUMBNAIL_MAX.height,
    imageAspectRatio: '1:1',
    helperText: `Thumbnail · PNG, JPEG, or WebP · max ${GROCERY_PRODUCT_IMAGE_THUMBNAIL_MAX.width}×${GROCERY_PRODUCT_IMAGE_THUMBNAIL_MAX.height}px · square preferred`,
  };
}

/** Default product images by code — replaces missing/404 seed Unsplash URLs. */
export const GROCERY_PRODUCT_IMAGE_DEFAULTS: Record<string, string> = {
  apple_1kg_thumb: squareImage('photo-1560806887-1e4cd0b6cbd6'),
  milk_1l_thumb: squareImage('photo-1563636619-e9143da7973b'),
  milk_1l_gallery: 'https://images.unsplash.com/photo-1550583724-b2692b85b150?w=800&auto=format',
  bread_thumb: squareImage('photo-1509440159596-0249088772ff'),
};

export const GENERIC_GROCERY_PRODUCT_IMAGE = squareImage('photo-1542838132-92c53300491e');

/** Legacy Unsplash IDs that 404 for seeded grocery product image rows. */
const BROKEN_GROCERY_PRODUCT_IMAGE_IDS = [
  'photo-1628088062854-d1870b45512f',
];

export function isBrokenGroceryProductImageUrl(url: string): boolean {
  const value = url.trim();
  if (!value) return false;
  return BROKEN_GROCERY_PRODUCT_IMAGE_IDS.some((id) => value.includes(id));
}

export function resolveGroceryProductSampleImageUrl(code: string, metadata?: Record<string, unknown>): string {
  const key = code.trim().toLowerCase();
  if (GROCERY_PRODUCT_IMAGE_DEFAULTS[key]) return GROCERY_PRODUCT_IMAGE_DEFAULTS[key];
  const sku = String(metadata?.skuCode ?? '').trim().toUpperCase();
  if (sku.includes('MILK')) return GROCERY_PRODUCT_IMAGE_DEFAULTS.milk_1l_thumb;
  if (sku.includes('APPLE')) return GROCERY_PRODUCT_IMAGE_DEFAULTS.apple_1kg_thumb;
  if (sku.includes('BRD') || sku.includes('BREAD')) return GROCERY_PRODUCT_IMAGE_DEFAULTS.bread_thumb;
  return GENERIC_GROCERY_PRODUCT_IMAGE;
}

export function resolveGroceryProductListImageUrl(
  code: string,
  metadata?: Record<string, unknown>,
): { primary: string; fallback: string } {
  const raw = String(metadata?.imageUrl ?? metadata?.icon ?? metadata?.image ?? '').trim();
  const defaultRaw = resolveGroceryProductSampleImageUrl(code, metadata);
  const defaultResolved = resolveAssetUrl(defaultRaw);
  const genericResolved = resolveAssetUrl(GENERIC_GROCERY_PRODUCT_IMAGE);

  if (raw && !isBrokenGroceryProductImageUrl(raw)) {
    return {
      primary: resolveAssetUrl(raw),
      fallback: defaultResolved !== genericResolved ? defaultResolved : genericResolved,
    };
  }
  if (defaultResolved) {
    return {
      primary: defaultResolved,
      fallback: genericResolved && genericResolved !== defaultResolved ? genericResolved : '',
    };
  }
  return { primary: raw ? resolveAssetUrl(raw) : '', fallback: '' };
}

export function resolveGroceryProductImageSources(
  code: string,
  metadata?: Record<string, unknown>,
): string[] {
  const { primary, fallback } = resolveGroceryProductListImageUrl(code, metadata);
  const generic = resolveAssetUrl(GENERIC_GROCERY_PRODUCT_IMAGE);
  const chain = [primary, fallback, generic, FOOD_ADDON_PLACEHOLDER_DATA_URI];
  return chain.filter((src, index) => src && chain.indexOf(src) === index);
}

/** Product Image Master list — Display Order ascending, then name. */
export function sortGroceryProductImageRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Product Image form — SKU / type / image; status/sort live on list toggle / basic info. */
export function enrichGroceryProductImageMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'skuCode') {
        return {
          ...field,
          required: true,
          label: field.label || 'Product SKU',
          placeholder: field.placeholder || 'e.g. MILK_1L',
          helperText: field.helperText
            || 'Must match an existing Grocery Product SKU (e.g. APPLE_1KG, MILK_1L, BRD-001)',
        };
      }
      if (field.key === 'imageType') {
        return {
          ...field,
          required: true,
          label: field.label || 'Image Type',
          helperText: field.helperText
            || 'Thumbnail: square product tile · Gallery: wider product photo',
          options: field.options?.length
            ? field.options
            : [
              { value: 'thumbnail', label: 'Thumbnail' },
              { value: 'gallery', label: 'Gallery' },
            ],
        };
      }
      if (field.key === 'imageUrl' || field.type === 'image') {
        const constraints = groceryProductImageConstraintsForType('thumbnail');
        return {
          ...field,
          type: 'image' as const,
          required: true,
          label: field.label || 'Image',
          imageMaxWidth: constraints.imageMaxWidth,
          imageMaxHeight: constraints.imageMaxHeight,
          imageAspectRatio: constraints.imageAspectRatio,
          helperText: constraints.helperText,
        };
      }
      return field;
    });
}

const GROCERY_PRODUCT_SKU_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{1,63}$/;

export function validateGroceryProductSkuCode(value: unknown): string | undefined {
  const sku = String(value ?? '').trim();
  if (!sku) return 'Product SKU is required';
  if (!GROCERY_PRODUCT_SKU_PATTERN.test(sku)) {
    return 'SKU must be 2–64 characters: letters, numbers, hyphen or underscore (e.g. MILK_1L)';
  }
  return undefined;
}

export function validateDuplicateGroceryProductImageSkuType(
  skuCode: unknown,
  imageType: unknown,
  records: Array<{ _id?: string; metadata?: Record<string, unknown> }> = [],
  editingId?: string,
): string | undefined {
  const sku = String(skuCode ?? '').trim().toUpperCase();
  const type = String(imageType ?? '').trim().toLowerCase();
  if (!sku || !type) return undefined;
  const duplicate = records.find((row) => {
    if (editingId && row._id === editingId) return false;
    const meta = row.metadata || {};
    return String(meta.skuCode ?? '').trim().toUpperCase() === sku
      && String(meta.imageType ?? '').trim().toLowerCase() === type;
  });
  if (duplicate) {
    return `An image of type "${type}" already exists for SKU ${sku}`;
  }
  return undefined;
}

export function validateGroceryProductImageFields(
  metadata: Record<string, unknown>,
  records: Array<{ _id?: string; metadata?: Record<string, unknown> }> = [],
  editingId?: string,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const skuErr = validateGroceryProductSkuCode(metadata.skuCode);
  if (skuErr) errors.skuCode = skuErr;

  const imageType = String(metadata.imageType ?? '').trim().toLowerCase();
  if (!imageType) {
    errors.imageType = 'Image Type is required';
  } else if (imageType !== 'thumbnail' && imageType !== 'gallery') {
    errors.imageType = 'Select Thumbnail or Gallery';
  }

  const imageUrl = String(metadata.imageUrl ?? '').trim();
  if (!imageUrl) {
    errors.imageUrl = 'Product image is required';
  } else if (isBrokenGroceryProductImageUrl(imageUrl)) {
    errors.imageUrl = 'This image URL is broken — upload a valid PNG, JPEG, or WebP';
  }

  if (!errors.skuCode && !errors.imageType) {
    const dup = validateDuplicateGroceryProductImageSkuType(
      metadata.skuCode,
      metadata.imageType,
      records,
      editingId,
    );
    if (dup) errors.skuCode = dup;
  }

  return errors;
}

/** Grocery availability schedules list — Display Order ascending, then name. */
export function sortGroceryAvailabilityScheduleRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

const TIME_HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isGroceryAvailabilityTimeRangeInvalid(
  metadata?: Record<string, unknown>,
): boolean {
  const start = String(metadata?.startTime ?? '').trim();
  const end = String(metadata?.endTime ?? '').trim();
  if (!start || !end || !TIME_HHMM.test(start) || !TIME_HHMM.test(end)) return false;
  return start >= end;
}

/** Availability Schedule form — day + start/end time selects; status/sort live on list. */
export function enrichGroceryAvailabilityScheduleMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'day') {
        return {
          ...field,
          required: true,
          label: field.label || 'Day',
          type: 'select' as const,
          helperText: field.helperText || 'Weekday this delivery window applies to',
          options: field.options?.length
            ? field.options
            : [
              { value: 'mon', label: 'Monday' }, { value: 'tue', label: 'Tuesday' },
              { value: 'wed', label: 'Wednesday' }, { value: 'thu', label: 'Thursday' },
              { value: 'fri', label: 'Friday' }, { value: 'sat', label: 'Saturday' },
              { value: 'sun', label: 'Sunday' },
            ],
        };
      }
      if (field.key === 'startTime') {
        return {
          ...field,
          required: true,
          label: field.label || 'Start Time',
          type: 'select' as const,
          helperText: field.helperText || 'Select start time — must be earlier than End Time',
          options: DELIVERY_TIME_OPTIONS.map((option) => ({ ...option })),
        };
      }
      if (field.key === 'endTime') {
        return {
          ...field,
          required: true,
          label: field.label || 'End Time',
          type: 'select' as const,
          helperText: field.helperText || 'Select end time — must be after Start Time',
          options: DELIVERY_TIME_OPTIONS.map((option) => ({ ...option })),
        };
      }
      return field;
    });
}

export function validateGroceryAvailabilityScheduleFields(
  metadata: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const day = String(metadata.day ?? '').trim().toLowerCase();
  if (!day) {
    errors.day = 'Day is required';
  }

  const start = String(metadata.startTime ?? '').trim();
  const end = String(metadata.endTime ?? '').trim();
  if (!start) {
    errors.startTime = 'Start time is required';
  } else if (!TIME_HHMM.test(start)) {
    errors.startTime = 'Select a valid start time (HH:mm)';
  }
  if (!end) {
    errors.endTime = 'End time is required';
  } else if (!TIME_HHMM.test(end)) {
    errors.endTime = 'Select a valid end time (HH:mm)';
  }
  if (
    start && end
    && TIME_HHMM.test(start)
    && TIME_HHMM.test(end)
    && start >= end
  ) {
    errors.endTime = 'End time must be after start time';
  }
  return errors;
}

export function sortDeliveryPartnerPricingRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

export function sortDeliveryIncentiveRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

export function sortDeliveryPartnerTierRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const orderA = resolveRecordSortOrder(a);
    const orderB = resolveRecordSortOrder(b);
    if (orderA !== orderB) {
      if (orderA === 0 && orderB === 0) {
        /* fall through to minOrders when neither has display order */
      } else if (orderA === 0) {
        return 1;
      } else if (orderB === 0) {
        return -1;
      } else {
        return orderA - orderB;
      }
    }
    const minA = Number(a.metadata?.minOrders);
    const minB = Number(b.metadata?.minOrders);
    const hasMinA = Number.isFinite(minA);
    const hasMinB = Number.isFinite(minB);
    if (hasMinA && hasMinB && minA !== minB) return minA - minB;
    if (hasMinA && !hasMinB) return -1;
    if (!hasMinA && hasMinB) return 1;
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });
}

/** Min Orders for list — marks 0 as the intentional base tier. */
export function formatDeliveryPartnerTierMinOrdersDisplay(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  if (num === 0) return '0 (base tier)';
  return String(Math.trunc(num));
}

/** Bonus % for list — clarifies it is percent of partner earnings. */
export function formatDeliveryPartnerTierBonusDisplay(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `${num}% of earnings`;
}

export function validateDeliveryPartnerTierMinOrders(value: unknown): string | undefined {
  if (value === undefined || value === null || String(value).trim() === '') {
    return 'Min Orders is required — use 0 for the base/starting tier';
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Min Orders must be a valid number';
  if (!Number.isInteger(num)) return 'Min Orders must be a whole number';
  if (num < 0) return 'Min Orders cannot be negative';
  if (num > 999999) return 'Min Orders cannot exceed 999999';
  return undefined;
}

export function validateDeliveryPartnerTierBonusPercent(value: unknown): string | undefined {
  if (value === undefined || value === null || String(value).trim() === '') {
    return 'Bonus (% of earnings) is required — use 0 if this tier has no earnings uplift';
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Bonus must be a valid number';
  if (num < 0) return 'Bonus cannot be negative';
  if (num > 100) return 'Bonus cannot exceed 100% of earnings';
  return undefined;
}

export function validateDuplicateDeliveryPartnerTierMinOrders(
  minOrders: unknown,
  existingRecords: Array<{ _id?: string; metadata?: Record<string, unknown> }> = [],
  editingId?: string,
): string | undefined {
  const value = Number(minOrders);
  if (!Number.isFinite(value)) return undefined;
  const duplicate = existingRecords.find((row) => {
    if (editingId && row._id === editingId) return false;
    const rowMin = Number(row.metadata?.minOrders);
    return Number.isFinite(rowMin) && rowMin === value;
  });
  if (!duplicate) return undefined;
  return `Min Orders ${value} is already used by another tier — thresholds must be unique`;
}

/**
 * Display Order must rise with Min Orders so Bronze → Silver → Gold stays consistent.
 * Lower display order = lower (or equal) threshold; higher display order = higher threshold.
 */
export function validateDeliveryPartnerTierSequence(
  minOrders: unknown,
  sortOrder: unknown,
  existingRecords: Array<{
    _id?: string;
    sortOrder?: number;
    metadata?: Record<string, unknown>;
  }> = [],
  editingId?: string,
): string | undefined {
  const min = Number(minOrders);
  const order = Number(sortOrder);
  if (!Number.isFinite(min) || !Number.isFinite(order) || order < 1) return undefined;

  for (const row of existingRecords) {
    if (editingId && row._id === editingId) continue;
    const rowMin = Number(row.metadata?.minOrders);
    const rowOrder = resolveRecordSortOrder(row);
    if (!Number.isFinite(rowMin) || !rowOrder) continue;

    if (order > rowOrder && min <= rowMin) {
      return `Display Order ${order} must have Min Orders greater than the previous tier (${rowMin}) — keep Bronze → Silver → Gold progression`;
    }
    if (order < rowOrder && min >= rowMin) {
      return `Display Order ${order} must have Min Orders less than the next tier (${rowMin}) — keep Bronze → Silver → Gold progression`;
    }
  }
  return undefined;
}

/** Explains non-overlapping tier assignment for admins. */
export function formatDeliveryPartnerTierExplanation(
  metadata?: Record<string, unknown>,
): string {
  const min = Number(metadata?.minOrders);
  const bonus = Number(metadata?.bonusPercent);
  const minLabel = Number.isFinite(min) ? String(Math.trunc(min)) : 'this threshold';
  const bonusLabel = Number.isFinite(bonus) ? `${bonus}%` : 'the bonus %';
  const baseNote = min === 0
    ? 'Min Orders 0 is the intentional base tier for new partners. '
    : '';
  return (
    `${baseNote}`
    + `Partners are assigned the highest active tier whose Min Orders they meet `
    + `(≥ ${minLabel} for this tier). Thresholds must not overlap or duplicate. `
    + `Bonus ${bonusLabel} is added to partner delivery earnings when this tier applies — not a flat ${getCurrencySymbol()} amount.`
  );
}

/** Partner tiers form — threshold/bonus helpers; status/sort stay on basic info. */
export function enrichDeliveryPartnerTierMetadataFields(
  fields: MasterFieldDef[],
): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'minOrders') {
        return {
          ...field,
          label: 'Min Orders',
          type: 'number' as const,
          required: true,
          min: 0,
          max: 999999,
          helperText:
            'Allowed range: 0–999999. Use 0 for the base/starting tier. '
            + 'Must be unique and increase with Display Order (Bronze → Silver → Gold).',
        };
      }
      if (field.key === 'bonusPercent') {
        return {
          ...field,
          label: 'Bonus (% of earnings)',
          type: 'percent' as const,
          required: true,
          min: 0,
          max: 100,
          helperText:
            'Allowed range: 0–100. Percentage uplift on partner delivery earnings when this tier applies '
            + `(e.g. 5 = +5% of earnings — not a flat ${getCurrencySymbol()} amount).`,
        };
      }
      return field;
    });
}

export const MERCHANT_DOCUMENT_CATEGORY_OPTIONS = [
  { value: 'tax', label: 'Tax' },
  { value: 'identity', label: 'Identity' },
  { value: 'license', label: 'License' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'other', label: 'Other' },
] as const;

export const MERCHANT_DOCUMENT_ENTITY_OPTIONS = [
  { value: 'merchant', label: 'Merchant' },
  { value: 'vendor', label: 'Vendor' },
] as const;

export const MERCHANT_DOCUMENT_ALLOWED_ENTITIES = new Set(
  MERCHANT_DOCUMENT_ENTITY_OPTIONS.map((option) => option.value),
);

function isTruthyMetaFlag(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

export function parseMerchantDocumentEntityTypes(value: unknown): string[] {
  return String(value ?? '')
    .split(/[,|]/)
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
}

export function sortMerchantDocumentRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

export function formatMerchantDocumentCategory(value: unknown): string {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '—';
  const match = MERCHANT_DOCUMENT_CATEGORY_OPTIONS.find((option) => option.value === raw);
  return match?.label || raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatMerchantDocumentEntityTypes(value: unknown): string {
  const parts = parseMerchantDocumentEntityTypes(value);
  if (!parts.length) return '—';
  return parts
    .map((part) => MERCHANT_DOCUMENT_ENTITY_OPTIONS.find((option) => option.value === part)?.label
      || part.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(', ');
}

/** Explains upload / document-number / expiry rules for list admins. */
export function formatMerchantDocumentComplianceRules(
  metadata?: Record<string, unknown>,
): string {
  const meta = metadata || {};
  const parts: string[] = [];
  parts.push(
    isTruthyMetaFlag(meta.required)
      ? 'Upload required for onboarding'
      : 'Upload optional',
  );
  parts.push(
    isTruthyMetaFlag(meta.identifierRequired)
      ? 'Document number required for verification'
      : 'Document number not required',
  );
  parts.push(
    isTruthyMetaFlag(meta.expiryRequired)
      ? 'Expiry date required (must stay valid)'
      : 'Expiry date not required',
  );
  return parts.join(' · ');
}

export function formatMerchantDocumentRuleFlag(
  fieldKey: string,
  value: unknown,
): string {
  const on = isTruthyMetaFlag(value);
  if (fieldKey === 'required') {
    return on ? 'Upload required for onboarding' : 'Upload optional';
  }
  if (fieldKey === 'identifierRequired') {
    return on ? 'Document number required for verification' : 'Document number not required';
  }
  if (fieldKey === 'expiryRequired') {
    return on ? 'Expiry date required (must stay valid)' : 'Expiry date not required';
  }
  return on ? 'Yes' : 'No';
}

/** Merchant / Vendor Documents form — category + compliance helpers. */
export function enrichMerchantDocumentMetadataFields(
  fields: MasterFieldDef[],
): MasterFieldDef[] {
  const mapped = fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'category') {
        return {
          ...field,
          label: 'Document Category',
          type: 'select' as const,
          required: true,
          helperText: 'Required — Tax, Identity, License, Compliance, or Other. Empty category cannot be saved.',
          options: MERCHANT_DOCUMENT_CATEGORY_OPTIONS.map((option) => ({ ...option })),
        };
      }
      if (field.key === 'required') {
        return {
          ...field,
          label: 'Upload Required',
          type: 'boolean' as const,
          helperText: 'When on, partners must upload this document during onboarding',
        };
      }
      if (field.key === 'identifierRequired') {
        return {
          ...field,
          label: 'Document Number Required',
          type: 'boolean' as const,
          helperText: 'When on, partners must enter the document/certificate number for verification',
        };
      }
      if (field.key === 'expiryRequired') {
        return {
          ...field,
          label: 'Expiry Date Required',
          type: 'boolean' as const,
          helperText: 'When on, partners must provide a valid expiry date for this document',
        };
      }
      if (field.key === 'entityTypes') {
        return {
          ...field,
          label: 'Applies To',
          type: 'select' as const,
          multiple: true,
          required: true,
          helperText: 'Select Merchant and/or Vendor — only these entity types are supported',
          options: MERCHANT_DOCUMENT_ENTITY_OPTIONS.map((option) => ({ ...option })),
        };
      }
      return field;
    });

  if (mapped.some((field) => field.key === 'category')) return mapped;
  return [
    {
      key: 'category',
      label: 'Document Category',
      type: 'select',
      required: true,
      helperText: 'Required — Tax, Identity, License, Compliance, or Other. Empty category cannot be saved.',
      options: MERCHANT_DOCUMENT_CATEGORY_OPTIONS.map((option) => ({ ...option })),
    },
    ...mapped,
  ];
}

export function validateMerchantDocumentFields(
  metadata?: Record<string, unknown>,
): Record<string, string> {
  const meta = metadata || {};
  const errors: Record<string, string> = {};
  const category = String(meta.category ?? '').trim().toLowerCase();
  const allowedCategories = new Set<string>(MERCHANT_DOCUMENT_CATEGORY_OPTIONS.map((option) => option.value));
  if (!category) {
    errors.category = 'Document Category is required';
  } else if (!allowedCategories.has(category)) {
    errors.category = 'Select Tax, Identity, License, Compliance, or Other';
  }

  const entities = parseMerchantDocumentEntityTypes(meta.entityTypes);
  if (!entities.length) {
    errors.entityTypes = 'Applies To is required — select Merchant and/or Vendor';
  } else {
    const invalid = entities.filter((entity) => !MERCHANT_DOCUMENT_ALLOWED_ENTITIES.has(entity));
    if (invalid.length) {
      errors.entityTypes = `Unsupported entity type(s): ${invalid.join(', ')} — only Merchant and Vendor are allowed`;
    }
  }
  return errors;
}

export const CANCELLATION_REASON_APPLIES_TO_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'merchant', label: 'Merchant' },
  { value: 'partner', label: 'Delivery Partner' },
  { value: 'all', label: 'All' },
] as const;

export const CANCELLATION_REASON_ALLOWED_APPLIES_TO = new Set<string>([
  ...CANCELLATION_REASON_APPLIES_TO_OPTIONS.map((option) => option.value),
  'driver', // legacy alias → Delivery Partner
]);

export function sortCancellationReasonRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Resolve Applies To from appliesTo or legacy userType. */
export function resolveCancellationReasonAppliesTo(
  metadata?: Record<string, unknown>,
): string {
  const meta = metadata || {};
  const raw = String(meta.appliesTo ?? meta.userType ?? '').trim().toLowerCase();
  if (raw === 'driver') return 'partner';
  return raw;
}

export function formatCancellationReasonAppliesTo(
  value: unknown,
  metadata?: Record<string, unknown>,
): string {
  const raw = String(value ?? '').trim().toLowerCase()
    || resolveCancellationReasonAppliesTo(metadata);
  if (!raw) return '—';
  if (raw === 'driver' || raw === 'partner') return 'Delivery Partner';
  const match = CANCELLATION_REASON_APPLIES_TO_OPTIONS.find((option) => option.value === raw);
  return match?.label || raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function enrichCancellationReasonMetadataFields(
  fields: MasterFieldDef[],
): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'appliesTo') {
        return {
          ...field,
          label: 'Applies To',
          type: 'select' as const,
          required: true,
          helperText: 'Who can select this reason — Customer, Merchant, Delivery Partner, or All',
          options: CANCELLATION_REASON_APPLIES_TO_OPTIONS.map((option) => ({ ...option })),
        };
      }
      return field;
    });
}

export function validateCancellationReasonFields(
  metadata?: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const appliesTo = resolveCancellationReasonAppliesTo(metadata);
  if (!appliesTo) {
    errors.appliesTo = 'Applies To is required — select Customer, Merchant, Delivery Partner, or All';
  } else if (!CANCELLATION_REASON_ALLOWED_APPLIES_TO.has(appliesTo)) {
    errors.appliesTo = 'Applies To must be Customer, Merchant, Delivery Partner, or All';
  }
  return errors;
}

export function sortSubstitutionRuleRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return sortPackagingTypeRows(rows);
}

/** Yes/No for Allow Substitute — avoids raw true/false in the list. */
export function formatSubstitutionAllowDisplay(value: unknown): string {
  if (value == null || value === '') return 'No — hidden on mobile';
  const allowed = value === true || value === 'true' || value === 1 || value === '1';
  return allowed ? 'Yes — shown on mobile' : 'No — hidden on mobile';
}

export function isSubstitutionAllowed(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

/** Explains mobile visibility of the substitution option for admins. */
export function formatSubstitutionRuleExplanation(
  metadata?: Record<string, unknown>,
): string {
  const allowed = isSubstitutionAllowed(metadata?.allowSubstitute);
  return allowed
    ? 'Allow Substitute is ON — this option is shown on the mobile app so customers can choose substitution when an item is unavailable.'
    : 'Allow Substitute is OFF — this option is hidden on the mobile app; customers will not see or select this substitution rule.';
}

export function enrichSubstitutionRuleMetadataFields(
  fields: MasterFieldDef[],
): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'allowSubstitute') {
        return {
          ...field,
          label: 'Allow Substitute',
          type: 'boolean' as const,
          required: true,
          helperText:
            'ON = shown on mobile (customers can use this rule). '
            + 'OFF = hidden on mobile (rule is not offered to customers).',
        };
      }
      return field;
    });
}

export function validateSubstitutionRuleFields(
  metadata?: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const raw = metadata?.allowSubstitute;
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    errors.allowSubstitute = 'Allow Substitute is required — turn ON to show on mobile, OFF to hide';
  }
  return errors;
}

/** Human-readable peak window for list/detail (or — when not Peak Hour). */
export function formatDeliveryIncentivePeakWindow(
  metadata?: Record<string, unknown>,
): string {
  if (!isDeliveryIncentivePeakHour(metadata?.incentiveType)) return '—';
  const start = String(metadata?.startTime ?? '').trim();
  const end = String(metadata?.endTime ?? '').trim();
  if (!start || !end) return 'Not configured';
  return `${start}–${end}`;
}

export function formatDeliveryIncentiveAmountDisplay(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `${formatMoney(num)} / order`;
}

export function formatDeliveryPartnerPricingServiceLabel(serviceSlug: unknown): string {
  const slug = String(serviceSlug ?? '').trim();
  return SERVICE_SLUG_LABELS[slug] || slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Meaningful display name from zone + service (avoids Test/Default placeholders). */
export function formatDeliveryPartnerPricingName(
  zoneCode: unknown,
  serviceSlug: unknown,
): string {
  const zone = String(zoneCode ?? '').trim().toUpperCase();
  const service = formatDeliveryPartnerPricingServiceLabel(serviceSlug);
  if (zone && service) return `${zone} ${service} Partner Pricing`;
  if (zone) return `${zone} Partner Pricing`;
  if (service) return `${service} Partner Pricing`;
  return '';
}

/** Meaningful description summarizing partner rate card fields. */
export function formatDeliveryPartnerPricingDescription(
  metadata?: Record<string, unknown>,
): string {
  const meta = metadata || {};
  const baseKm = Number(meta.baseKm);
  const baseAmount = Number(meta.baseAmount);
  const remainingKmRate = Number(meta.remainingKmRate);
  const pickupCharge = Number(meta.pickupCharge);
  const dropCharge = Number(meta.dropCharge);
  const minimumGuarantee = Number(meta.minimumGuarantee);
  const maximumDeliveryKm = Number(meta.maximumDeliveryKm);
  const parts: string[] = [];
  if (Number.isFinite(baseAmount) && Number.isFinite(baseKm) && baseKm > 0) {
    parts.push(`Base ${getCurrencySymbol()}${baseAmount} for first ${baseKm} km`);
  } else if (Number.isFinite(baseAmount)) {
    parts.push(`Base ${getCurrencySymbol()}${baseAmount}`);
  }
  if (Number.isFinite(remainingKmRate)) {
    parts.push(`${getCurrencySymbol()}${remainingKmRate}/km after`);
  }
  if (Number.isFinite(pickupCharge) && pickupCharge > 0) {
    parts.push(`pickup ${getCurrencySymbol()}${pickupCharge}`);
  }
  if (Number.isFinite(dropCharge) && dropCharge > 0) {
    parts.push(`drop ${getCurrencySymbol()}${dropCharge}`);
  }
  if (Number.isFinite(minimumGuarantee) && minimumGuarantee > 0) {
    parts.push(`min guarantee ${getCurrencySymbol()}${minimumGuarantee}`);
  }
  if (Number.isFinite(maximumDeliveryKm) && maximumDeliveryKm > 0) {
    parts.push(`max ${maximumDeliveryKm} km`);
  }
  return parts.join(' · ');
}

export function isPlaceholderDeliveryPartnerPricingName(name?: string): boolean {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return true;
  if (PLACEHOLDER_PRICING_NAME_RE.test(trimmed)) return true;
  return /^(test|default|sample|demo)(\s+(pricing|name|record))?$/i.test(trimmed);
}

export function isPlaceholderDeliveryPartnerPricingDescription(description?: string): boolean {
  const trimmed = String(description ?? '').trim();
  if (!trimmed) return true;
  if (PLACEHOLDER_PRICING_DESC_RE.test(trimmed)) return true;
  if (validateOptionalDescription(trimmed)) return true;
  return trimmed.length < 12;
}

export function validateDeliveryPartnerPricingName(name?: string): string | undefined {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return 'Display name is required';
  if (isPlaceholderDeliveryPartnerPricingName(trimmed)) {
    return 'Use a meaningful name (e.g. WORLD Food Delivery Partner Pricing) — not Test or Default';
  }
  return undefined;
}

export function validateDeliveryPartnerPricingDescription(description?: string): string | undefined {
  const trimmed = String(description ?? '').trim();
  if (!trimmed) {
    return 'Description is required — summarize base km, rates, and guarantees';
  }
  const spamErr = validateOptionalDescription(trimmed);
  if (spamErr) return spamErr;
  if (isPlaceholderDeliveryPartnerPricingDescription(trimmed)) {
    return `Description must include meaningful pricing details (e.g. Base ${getCurrencySymbol()}35 for first 3 km · ${getCurrencySymbol()}6/km after)`;
  }
  return undefined;
}

function decimalPlacesOk(value: number, maxDecimals: number): boolean {
  const factor = 10 ** maxDecimals;
  return Math.abs(value * factor - Math.round(value * factor)) <= 1e-6;
}

export function validatePartnerPricingAmount(
  value: unknown,
  label: string,
  opts?: { required?: boolean },
): string | undefined {
  if (value === '' || value == null) {
    return opts?.required ? `${label} is required` : undefined;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return `${label} must be a valid number`;
  if (num < PARTNER_PRICING_AMOUNT_MIN) return `${label} cannot be negative`;
  if (num > PARTNER_PRICING_AMOUNT_MAX) {
    return `${label} cannot exceed ${getCurrencySymbol()}${PARTNER_PRICING_AMOUNT_MAX.toLocaleString('en-IN')}`;
  }
  if (!decimalPlacesOk(num, PARTNER_PRICING_AMOUNT_DECIMALS)) {
    return `${label} allows at most ${PARTNER_PRICING_AMOUNT_DECIMALS} decimal places`;
  }
  return undefined;
}

export function validatePartnerPricingKm(
  value: unknown,
  label: string,
  opts?: { required?: boolean },
): string | undefined {
  if (value === '' || value == null) {
    return opts?.required ? `${label} is required` : undefined;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return `${label} must be a valid number`;
  if (num < PARTNER_PRICING_KM_MIN) return `${label} cannot be negative`;
  if (num > PARTNER_PRICING_KM_MAX) {
    return `${label} cannot exceed ${PARTNER_PRICING_KM_MAX.toLocaleString('en-IN')} km`;
  }
  if (!decimalPlacesOk(num, PARTNER_PRICING_KM_DECIMALS)) {
    return `${label} allows at most ${PARTNER_PRICING_KM_DECIMALS} decimal places`;
  }
  return undefined;
}

export function validateDuplicateDeliveryPartnerPricingZoneService(
  zoneCode: unknown,
  serviceSlug: unknown,
  existingRecords: Array<{ _id?: string; metadata?: Record<string, unknown> }> = [],
  editingId?: string,
): string | undefined {
  const zone = String(zoneCode ?? '').trim().toLowerCase();
  const service = String(serviceSlug ?? '').trim().toLowerCase();
  if (!zone || !service) return undefined;
  const duplicate = existingRecords.find((row) => {
    if (editingId && row._id === editingId) return false;
    const rowZone = String(row.metadata?.zoneCode ?? '').trim().toLowerCase();
    const rowService = String(row.metadata?.serviceSlug ?? '').trim().toLowerCase();
    return rowZone === zone && rowService === service;
  });
  if (!duplicate) return undefined;
  const zoneLabel = String(zoneCode ?? '').trim().toUpperCase() || zone;
  const serviceLabel = formatDeliveryPartnerPricingServiceLabel(serviceSlug);
  return `Partner pricing for ${zoneLabel} + ${serviceLabel} already exists — use one rate card per zone and service`;
}

/** Field-level partner pricing rules — keys without the meta: prefix. */
export function validateDeliveryPartnerPricingFields(
  metadata?: Record<string, unknown>,
): Record<string, string> {
  const meta = metadata || {};
  const errors: Record<string, string> = {};

  const baseKmErr = validatePartnerPricingKm(meta.baseKm, 'Base KM Included', { required: true });
  if (baseKmErr) errors.baseKm = baseKmErr;
  else if (Number(meta.baseKm) < PARTNER_PRICING_BASE_KM_MIN) {
    errors.baseKm = `Base KM Included must be at least ${PARTNER_PRICING_BASE_KM_MIN}`;
  } else if (Number(meta.baseKm) > PARTNER_PRICING_KM_MAX) {
    errors.baseKm = `Base KM Included cannot exceed ${PARTNER_PRICING_KM_MAX.toLocaleString('en-IN')} km`;
  }

  const amountFields: Array<[string, string, boolean]> = [
    ['baseAmount', chargeFieldLabel('Base Amount / order'), true],
    ['remainingKmRate', chargeFieldLabel('Remaining KM Rate / km'), true],
    ['pickupCharge', chargeFieldLabel('Pickup Charge / order'), false],
    ['dropCharge', chargeFieldLabel('Drop Charge / order'), false],
    ['waitingChargePerMinute', chargeFieldLabel('Waiting Charge / minute'), false],
    ['minimumGuarantee', chargeFieldLabel('Minimum Guarantee / order'), false],
  ];
  for (const [key, label, required] of amountFields) {
    const err = validatePartnerPricingAmount(meta[key], label, { required });
    if (err) errors[key] = err;
  }

  if (meta.maximumDeliveryKm !== '' && meta.maximumDeliveryKm != null) {
    const maxKmErr = validatePartnerPricingKm(meta.maximumDeliveryKm, 'Maximum Delivery KM');
    if (maxKmErr) errors.maximumDeliveryKm = maxKmErr;
  }

  const baseKm = Number(meta.baseKm);
  const maxKm = Number(meta.maximumDeliveryKm);
  if (
    !errors.baseKm
    && !errors.maximumDeliveryKm
    && Number.isFinite(baseKm)
    && Number.isFinite(maxKm)
    && String(meta.maximumDeliveryKm ?? '').trim() !== ''
    && maxKm < baseKm
  ) {
    errors.maximumDeliveryKm = `Maximum Delivery KM must be at least Base KM Included (${baseKm})`;
  }

  const baseAmount = Number(meta.baseAmount);
  const minimumGuarantee = Number(meta.minimumGuarantee);
  if (
    !errors.baseAmount
    && !errors.minimumGuarantee
    && Number.isFinite(baseAmount)
    && Number.isFinite(minimumGuarantee)
    && String(meta.minimumGuarantee ?? '').trim() !== ''
    && minimumGuarantee > 0
    && minimumGuarantee < baseAmount
  ) {
    const message = `Minimum Guarantee (${getCurrencySymbol()}${minimumGuarantee}) must be at least Base Amount (${getCurrencySymbol()}${baseAmount})`;
    errors.minimumGuarantee = message;
    errors.baseAmount = `Base Amount (${getCurrencySymbol()}${baseAmount}) cannot exceed Minimum Guarantee (${getCurrencySymbol()}${minimumGuarantee})`;
  }

  return errors;
}

/** Live relationship copy for Minimum Guarantee vs Base Amount (error or guidance). */
export function formatDeliveryPartnerPricingGuaranteeHint(
  metadata?: Record<string, unknown>,
): { severity: 'error' | 'info'; message: string } | undefined {
  const meta = metadata || {};
  const baseRaw = meta.baseAmount;
  const guaranteeRaw = meta.minimumGuarantee;
  if (baseRaw === '' || baseRaw == null || guaranteeRaw === '' || guaranteeRaw == null) {
    return {
      severity: 'info',
      message: `Minimum Guarantee must be at least Base Amount when set (e.g. Base ${getCurrencySymbol()}10 → Minimum Guarantee ${getCurrencySymbol()}10 or higher).`,
    };
  }
  const baseAmount = Number(baseRaw);
  const minimumGuarantee = Number(guaranteeRaw);
  if (!Number.isFinite(baseAmount) || !Number.isFinite(minimumGuarantee)) return undefined;
  if (minimumGuarantee <= 0) {
    return {
      severity: 'info',
      message: `Minimum Guarantee is off (${getCurrencySymbol()}0). Set it to at least Base Amount to enforce a payout floor.`,
    };
  }
  if (minimumGuarantee < baseAmount) {
    return {
      severity: 'error',
      message: `Minimum Guarantee (${getCurrencySymbol()}${minimumGuarantee}) is below Base Amount (${getCurrencySymbol()}${baseAmount}). Raise Minimum Guarantee to at least ${getCurrencySymbol()}${baseAmount}.`,
    };
  }
  return {
    severity: 'info',
    message: `Minimum Guarantee (${getCurrencySymbol()}${minimumGuarantee}) is at least Base Amount (${getCurrencySymbol()}${baseAmount}) — payout will not fall below ${getCurrencySymbol()}${minimumGuarantee}.`,
  };
}

/** Example earnings breakdown for admins configuring the rate card. */
export function formatDeliveryPartnerPricingExample(
  metadata?: Record<string, unknown>,
  exampleDistanceKm = PARTNER_PRICING_EXAMPLE_DISTANCE_KM,
): string {
  const meta = metadata || {};
  const baseKm = Number(meta.baseKm);
  const baseAmount = Number(meta.baseAmount);
  const remainingKmRate = Number(meta.remainingKmRate);
  const pickupCharge = Number(meta.pickupCharge) || 0;
  const dropCharge = Number(meta.dropCharge) || 0;
  const minimumGuarantee = Number(meta.minimumGuarantee) || 0;
  if (![baseKm, baseAmount, remainingKmRate].every((n) => Number.isFinite(n))) {
    return 'Enter Base KM, Base Amount, and Remaining KM Rate to preview partner earnings.';
  }
  const remainingKm = Math.max(0, exampleDistanceKm - baseKm);
  const remainingCharge = remainingKm * remainingKmRate;
  const subtotal = baseAmount + pickupCharge + dropCharge + remainingCharge;
  const earnings = minimumGuarantee > 0 && subtotal < minimumGuarantee ? minimumGuarantee : subtotal;
  const guaranteeNote = minimumGuarantee > 0 && subtotal < minimumGuarantee
    ? ` · minimum guarantee raises payout to ${getCurrencySymbol()}${minimumGuarantee}`
    : '';
  const distancePart = remainingKm > 0
    ? `Base ${getCurrencySymbol()}${baseAmount} (${baseKm} km) + ${getCurrencySymbol()}${remainingKmRate}/km × ${remainingKm} km = ${getCurrencySymbol()}${remainingCharge}`
    : `Base ${getCurrencySymbol()}${baseAmount} covers the full ${exampleDistanceKm} km`;
  return (
    `${exampleDistanceKm} km example: ${distancePart}`
    + ` · pickup ${getCurrencySymbol()}${pickupCharge} + drop ${getCurrencySymbol()}${dropCharge}`
    + ` → earnings ${getCurrencySymbol()}${earnings}${guaranteeNote}`
  );
}

export function isDeliverySlotTimeRangeInvalid(
  metadata?: Record<string, unknown>,
): boolean {
  const start = resolveDeliverySlotStart({ metadata });
  const end = resolveDeliverySlotEnd({ metadata });
  if (!start || !end) return false;
  return start >= end;
}

export function validateDeliverySlotFee(value: unknown): string | undefined {
  if (value === '' || value == null) {
    return `Slot fee is required — use ${getCurrencySymbol()}0.00 for a free delivery slot`;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Slot fee must be a number';
  if (num < DELIVERY_SLOT_FEE_MIN) return `Slot fee cannot be negative — use ${getCurrencySymbol()}0.00 for free`;
  if (num > DELIVERY_SLOT_FEE_MAX) {
    return `Slot fee must be at most ${getCurrencySymbol()}${DELIVERY_SLOT_FEE_MAX.toLocaleString('en-IN')}`;
  }
  return undefined;
}

export function validatePackagingFee(value: unknown): string | undefined {
  if (value === '' || value == null) {
    return `Packaging fee is required — use ${getCurrencySymbol()}0.00 for free packaging`;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Packaging fee must be a number';
  if (num < PACKAGING_FEE_MIN) return `Packaging fee cannot be negative — use ${getCurrencySymbol()}0.00 for free`;
  if (num > PACKAGING_FEE_MAX) {
    return `Packaging fee must be at most ${getCurrencySymbol()}${PACKAGING_FEE_MAX.toLocaleString('en-IN')}`;
  }
  return undefined;
}

/** Delivery slot form: start/end dropdowns + fee — status/sort order live elsewhere. */
export function enrichDeliverySlotMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'start' || field.key === 'startTime') {
        return {
          ...field,
          key: 'start',
          label: 'Start Time',
          type: 'select' as const,
          required: true,
          helperText: 'Select start time — must be earlier than End Time',
          options: DELIVERY_TIME_OPTIONS.map((option) => ({ ...option })),
        };
      }
      if (field.key === 'end' || field.key === 'endTime') {
        return {
          ...field,
          key: 'end',
          label: 'End Time',
          type: 'select' as const,
          required: true,
          helperText: 'Select end time — must be after Start Time',
          options: DELIVERY_TIME_OPTIONS.map((option) => ({ ...option })),
        };
      }
      if (field.key === 'fee') {
        return {
          ...field,
          label: chargeFieldLabel('Slot Fee'),
          type: 'currency' as const,
          required: true,
          min: DELIVERY_SLOT_FEE_MIN,
          max: DELIVERY_SLOT_FEE_MAX,
          helperText: field.helperText
            || `${getCurrencySymbol()}0.00 = free · paid slots e.g. Express ${getCurrencySymbol()}29.00 · max ${getCurrencySymbol()}${DELIVERY_SLOT_FEE_MAX.toLocaleString('en-IN')}`,
        };
      }
      return field;
    });
}

/** Packaging type form: image + fee — status/sort order live elsewhere. */
export function enrichPackagingTypeMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'imageUrl' || field.type === 'image') {
        return {
          ...field,
          key: 'imageUrl',
          label: 'Packaging Image',
          type: 'image' as const,
          required: true,
          helperText: 'PNG, JPEG, or WebP — required packaging image',
        };
      }
      if (field.key === 'fee') {
        return {
          ...field,
          label: chargeFieldLabel('Packaging Fee'),
          type: 'currency' as const,
          required: true,
          min: PACKAGING_FEE_MIN,
          max: PACKAGING_FEE_MAX,
          helperText: field.helperText
            || `${getCurrencySymbol()}0.00 = free · paid types e.g. Eco ${getCurrencySymbol()}5.00 · max ${getCurrencySymbol()}${PACKAGING_FEE_MAX.toLocaleString('en-IN')}`,
        };
      }
      return field;
    });
}

/** Partner pricing form — status/sort order live on the basic-info / list sections.
 * When `preferredServiceSlug` is food/grocery (embedded service masters), Service is omitted —
 * it would only show static text matching the current hub. */
export function enrichDeliveryPartnerPricingMetadataFields(
  fields: MasterFieldDef[],
  preferredServiceSlug?: string,
): MasterFieldDef[] {
  const kmHelper = `Required · ${PARTNER_PRICING_BASE_KM_MIN}–${PARTNER_PRICING_KM_MAX.toLocaleString('en-IN')} km · up to ${PARTNER_PRICING_KM_DECIMALS} decimal places`;
  const amountHelper = `Non-negative · max ${getCurrencySymbol()}${PARTNER_PRICING_AMOUNT_MAX.toLocaleString('en-IN')} · up to ${PARTNER_PRICING_AMOUNT_DECIMALS} decimal places`;
  const maxKmHelper = `Optional · 0–${PARTNER_PRICING_KM_MAX.toLocaleString('en-IN')} km · must be ≥ Base KM Included`;
  const hideServiceField = preferredServiceSlug === 'food-delivery'
    || preferredServiceSlug === 'grocery-delivery';

  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .filter((field) => !(hideServiceField && field.key === 'serviceSlug'))
    .map((field) => {
      if (field.key === 'serviceSlug') {
        return {
          ...field,
          label: 'Service',
          required: true,
          helperText: 'Select Food Delivery or Grocery Delivery — one rate card per zone and service',
          options: [
            { value: 'food-delivery', label: 'Food Delivery' },
            { value: 'grocery-delivery', label: 'Grocery Delivery' },
          ],
        };
      }
      if (field.key === 'zoneCode') {
        return {
          ...field,
          label: 'Zone',
          required: true,
          helperText: 'Operational zone for this partner rate card',
        };
      }
      if (field.key === 'baseKm') {
        return {
          ...field,
          label: 'Base KM Included',
          type: 'number' as const,
          required: true,
          min: PARTNER_PRICING_BASE_KM_MIN,
          max: PARTNER_PRICING_KM_MAX,
          helperText: `${kmHelper} — distance covered by the base amount`,
        };
      }
      if (field.key === 'baseAmount') {
        return {
          ...field,
          label: chargeFieldLabel('Base Amount / order'),
          type: 'currency' as const,
          required: true,
          min: 0,
          max: PARTNER_PRICING_AMOUNT_MAX,
          helperText: `${amountHelper} — flat partner payout for Base KM Included`,
        };
      }
      if (field.key === 'remainingKmRate') {
        return {
          ...field,
          label: chargeFieldLabel('Remaining KM Rate / km'),
          type: 'currency' as const,
          required: true,
          min: 0,
          max: PARTNER_PRICING_AMOUNT_MAX,
          helperText: `${amountHelper} — charged per km beyond Base KM Included`,
        };
      }
      if (field.key === 'pickupCharge') {
        return {
          ...field,
          label: chargeFieldLabel('Pickup Charge / order'),
          type: 'currency' as const,
          min: 0,
          max: PARTNER_PRICING_AMOUNT_MAX,
          helperText: `${amountHelper} — added once per delivery pickup`,
        };
      }
      if (field.key === 'dropCharge') {
        return {
          ...field,
          label: chargeFieldLabel('Drop Charge / order'),
          type: 'currency' as const,
          min: 0,
          max: PARTNER_PRICING_AMOUNT_MAX,
          helperText: `${amountHelper} — added once per delivery drop-off`,
        };
      }
      if (field.key === 'waitingChargePerMinute') {
        return {
          ...field,
          label: chargeFieldLabel('Waiting Charge / minute'),
          type: 'currency' as const,
          min: 0,
          max: PARTNER_PRICING_AMOUNT_MAX,
          helperText: `${amountHelper} — billed for each waiting minute`,
        };
      }
      if (field.key === 'minimumGuarantee') {
        return {
          ...field,
          label: chargeFieldLabel('Minimum Guarantee / order'),
          type: 'currency' as const,
          min: 0,
          max: PARTNER_PRICING_AMOUNT_MAX,
          helperText: `${amountHelper} — must be ≥ Base Amount (example: Base ${getCurrencySymbol()}10 → Minimum Guarantee ${getCurrencySymbol()}10, ${getCurrencySymbol()}20, …)`,
        };
      }
      if (field.key === 'maximumDeliveryKm') {
        return {
          ...field,
          label: 'Maximum Delivery KM',
          type: 'number' as const,
          min: 0,
          max: PARTNER_PRICING_KM_MAX,
          helperText: `${maxKmHelper}; longer trips are rejected`,
        };
      }
      return field;
    });
}

export function resolveDeliveryServiceSlugDefault(hubServiceSlug?: string): 'food-delivery' | 'grocery-delivery' {
  return hubServiceSlug === 'grocery-delivery' ? 'grocery-delivery' : 'food-delivery';
}

export function deliveryServiceHelperText(preferredServiceSlug?: string): string {
  if (preferredServiceSlug === 'grocery-delivery') {
    return 'Select Grocery Delivery for this Grocery module incentive';
  }
  if (preferredServiceSlug === 'food-delivery') {
    return 'Select Food Delivery for this Food module incentive';
  }
  return 'Select Food Delivery or Grocery Delivery — must match the service this incentive applies to';
}

export function validateDeliveryIncentiveAmount(value: unknown): string | undefined {
  if (value === '' || value == null) {
    return `Amount is required — enter a positive ${getCurrencySymbol()} / order bonus`;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Amount must be a valid number';
  if (num <= 0) return `Amount must be greater than ${getCurrencySymbol()}0.00`;
  if (num > PARTNER_PRICING_AMOUNT_MAX) {
    return `Amount cannot exceed ${getCurrencySymbol()}${PARTNER_PRICING_AMOUNT_MAX.toLocaleString('en-IN')}`;
  }
  const factor = 10 ** PARTNER_PRICING_AMOUNT_DECIMALS;
  if (Math.abs(num * factor - Math.round(num * factor)) > 1e-6) {
    return `Amount allows at most ${PARTNER_PRICING_AMOUNT_DECIMALS} decimal places`;
  }
  return undefined;
}

export function isDeliveryIncentivePeakHour(incentiveType: unknown): boolean {
  return String(incentiveType ?? '').trim().toLowerCase() === 'peak_hour';
}

export function validateDeliveryIncentivePeakTimeRange(
  metadata?: Record<string, unknown>,
): Record<string, string> {
  const meta = metadata || {};
  if (!isDeliveryIncentivePeakHour(meta.incentiveType)) return {};
  const errors: Record<string, string> = {};
  const start = String(meta.startTime ?? '').trim();
  const end = String(meta.endTime ?? '').trim();
  if (!start) errors.startTime = 'Start Time is required for Peak Hour incentives';
  if (!end) errors.endTime = 'End Time is required for Peak Hour incentives';
  if (start && end && start >= end) {
    errors.endTime = 'End Time must be after Start Time';
  }
  return errors;
}

/** Explains how the incentive amount is applied for admins. */
export function formatDeliveryIncentiveExplanation(
  metadata?: Record<string, unknown>,
): string {
  const meta = metadata || {};
  const type = String(meta.incentiveType ?? '').trim().toLowerCase();
  const amount = Number(meta.amount);
  const amountLabel = Number.isFinite(amount) && amount > 0
    ? `${getCurrencySymbol()}${amount}`
    : 'the Amount';
  const start = String(meta.startTime ?? '').trim();
  const end = String(meta.endTime ?? '').trim();

  switch (type) {
    case 'peak_hour':
      return start && end
        ? `${amountLabel} is added once per completed delivery order when pickup falls between ${start} and ${end}.`
        : `${amountLabel} is added once per completed delivery order during the configured peak-hour window (set Start/End Time below).`;
    case 'rain':
      return `${amountLabel} is added once per completed delivery order when rain conditions apply.`;
    case 'night':
      return `${amountLabel} is added once per completed delivery order during night hours (22:00–06:00 by default).`;
    case 'heavy_order':
      return `${amountLabel} is added once per completed delivery order when the order is marked heavy (e.g. high cart value).`;
    case 'multi_order':
      return `${amountLabel} is added once per completed delivery order when the partner is on a multi-order batch.`;
    case 'festival':
      return `${amountLabel} is added once per completed delivery order during festival / special event periods.`;
    default:
      return `${amountLabel} is a flat ${getCurrencySymbol()} / order bonus added to partner earnings for each qualifying completed delivery — not per hour or per km.`;
  }
}

/** Delivery incentives form — service context + amount unit/validation helpers.
 * When embedded under Food/Grocery masters, hide Service (auto-set; static otherwise). */
export function enrichDeliveryIncentiveMetadataFields(
  fields: MasterFieldDef[],
  preferredServiceSlug?: string,
): MasterFieldDef[] {
  const serviceDefault = resolveDeliveryServiceSlugDefault(preferredServiceSlug);
  const amountHelper = `Positive amount · max ${getCurrencySymbol()}${PARTNER_PRICING_AMOUNT_MAX.toLocaleString('en-IN')} · up to ${PARTNER_PRICING_AMOUNT_DECIMALS} decimal places`;
  const hideServiceField = preferredServiceSlug === 'food-delivery'
    || preferredServiceSlug === 'grocery-delivery';

  const baseFields = fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .filter((field) => !(hideServiceField && field.key === 'serviceSlug'))
    .map((field) => {
      if (field.key === 'serviceSlug') {
        const foodFirst = [
          { value: 'food-delivery', label: 'Food Delivery' },
          { value: 'grocery-delivery', label: 'Grocery Delivery' },
        ];
        const groceryFirst = [
          { value: 'grocery-delivery', label: 'Grocery Delivery' },
          { value: 'food-delivery', label: 'Food Delivery' },
        ];
        return {
          ...field,
          label: 'Service',
          required: true,
          helperText: deliveryServiceHelperText(preferredServiceSlug),
          options: serviceDefault === 'grocery-delivery' ? groceryFirst : foodFirst,
        };
      }
      if (field.key === 'amount') {
        return {
          ...field,
          label: chargeFieldLabel('Amount / order'),
          type: 'currency' as const,
          required: true,
          min: 0.01,
          max: PARTNER_PRICING_AMOUNT_MAX,
          helperText: `${amountHelper} — flat bonus per qualifying completed delivery order (not per hour)`,
        };
      }
      if (field.key === 'incentiveType') {
        return {
          ...field,
          label: 'Incentive Type',
          required: true,
          helperText: 'Peak Hour shows Start/End Time; other types apply from operational flags',
        };
      }
      if (field.key === 'zoneCode') {
        return {
          ...field,
          label: 'Zone',
          required: true,
          helperText: 'Operational zone for this incentive',
        };
      }
      if (field.key === 'startTime' || field.key === 'start') {
        return {
          ...field,
          key: 'startTime',
          label: 'Peak Start Time',
          type: 'select' as const,
          required: false,
          helperText: 'Peak Hour window start — must be earlier than End Time',
          options: DELIVERY_TIME_OPTIONS.map((option) => ({ ...option })),
        };
      }
      if (field.key === 'endTime' || field.key === 'end') {
        return {
          ...field,
          key: 'endTime',
          label: 'Peak End Time',
          type: 'select' as const,
          required: false,
          helperText: 'Peak Hour window end — must be after Start Time',
          options: DELIVERY_TIME_OPTIONS.map((option) => ({ ...option })),
        };
      }
      return field;
    });

  const hasStart = baseFields.some((field) => field.key === 'startTime');
  const hasEnd = baseFields.some((field) => field.key === 'endTime');
  if (hasStart && hasEnd) return baseFields;

  const injected: MasterFieldDef[] = [];
  if (!hasStart) {
    injected.push({
      key: 'startTime',
      label: 'Peak Start Time',
      type: 'select',
      required: false,
      helperText: 'Peak Hour window start — must be earlier than End Time',
      options: DELIVERY_TIME_OPTIONS.map((option) => ({ ...option })),
    });
  }
  if (!hasEnd) {
    injected.push({
      key: 'endTime',
      label: 'Peak End Time',
      type: 'select',
      required: false,
      helperText: 'Peak Hour window end — must be after Start Time',
      options: DELIVERY_TIME_OPTIONS.map((option) => ({ ...option })),
    });
  }

  const amountIdx = baseFields.findIndex((field) => field.key === 'amount');
  if (amountIdx >= 0) {
    return [
      ...baseFields.slice(0, amountIdx),
      ...injected,
      ...baseFields.slice(amountIdx),
    ];
  }
  return [...baseFields, ...injected];
}
